import { compose, type Dispatch } from 'redux';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { reduce } from '@/utils/structuresManagment';
import { translate } from '@/i18n/static';
import { createSelector as reselectCreateSelector, createSelectorCreator } from 'reselect';
import { Observable } from 'rxjs';
import { setScrolledBottom, setScrolledTop } from '@/store/app-config/app-config.actions';
import { isOnSSR } from '@/utils/isOnSsr';
import Axios, { type AxiosResponse, type CancelTokenSource } from 'axios';
import { type ISelectOptions } from '@/components/dynamic-field/dynamic-select/DynamicSelect';
import { type IAppState } from './root-reducer';
import { type AppAxios } from '@/typings/global.interfaces';
import { appConfig } from '@/appConfig';

export const composeEnhancers =
    ((appConfig().NODE_ENV === 'development' || appConfig().ENABLED_REDUX_DEVTOOLS) &&
        window &&
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true, limit: 50 })) ||
    compose;

export const enableBodyScrollLock = (selector: string, isId?: boolean) => {
    const selectorByAttributeType = isId ? selector : `.${selector}`;
    const contentTarget = document.querySelector(selectorByAttributeType);
    enableBodyScroll(contentTarget);
};

export const disableBodyScrollLock = (selector: string, isId?: boolean) => {
    const selectorByAttributeType = isId ? selector : `.${selector}`;
    const contentTarget = document.querySelector(selectorByAttributeType);
    disableBodyScroll(contentTarget);
};

/**
 * Get first error from first field that is listed on set of errors inside children property
 * @param response
 * @param lang
 * @param shouldDisplayOnlyMessage
 */
export const getFormResponseError = (lang: string, response?: AxiosResponse, shouldDisplayOnlyMessage?: boolean) => {
    if (response && response.data && response.data.message) {
        return response.data.message;
    }

    if (response?.data?.errors?.errors && shouldDisplayOnlyMessage) {
        return response.data.errors.errors?.[0];
    }

    if (response && response.data && response.data.errors && response.data.errors.children) {
        const [error] = reduce(
            response.data.errors?.children,
            (fieldErrors: any, field) => {
                const [fieldError] = (field && field?.errors) || [];

                return [...fieldErrors, fieldError];
            },
            []
        );

        return error;
    }

    return translate(lang, 'utils.error.unknownError');
};

/**
 * Check if provided select options is currently selected
 */
export const isCurrentlySelected = (selectedOptions: ISelectOptions[], value: any) => {
    return selectedOptions.some(selectedOption => selectedOption.value === value);
};

export const getState$ = (state$: any): IAppState => {
    if (state$.source && state$.source.value) {
        return state$.source.value;
    }

    return state$.value;
};

export const createSelector = !isOnSSR() ? reselectCreateSelector : createSelectorCreator(func => func);

export function createCancelablePromise<Payload, Return>(
    fnPromise: ($http: AppAxios, payload: Payload, cancelTokenSource: CancelTokenSource) => Promise<Return>,
    $http: AppAxios,
    payload: Payload
): Observable<Return> {
    return new Observable(observer => {
        const cancelTokenSource = Axios.CancelToken.source();

        fnPromise($http, payload, cancelTokenSource).then(
            response => {
                observer.next(response);
                observer.complete();
            },
            error => {
                observer.error(error);
            }
        );

        return () => cancelTokenSource.cancel();
    });
}

export const dispatchSetScrolledTopAction = (dispatch: Dispatch, scrolledBottom: boolean) => {
    if (scrolledBottom) {
        dispatch(setScrolledTop());
    }
};

export const dispatchSetScrolledBottomAction = (dispatch: Dispatch, scrolledBottom: boolean) => {
    if (!scrolledBottom) {
        dispatch(setScrolledBottom());
    }
};
